import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BookOpen } from "lucide-react";

const RULES_API = `${process.env.REACT_APP_BACKEND_URL}/api/operations/rules-doc`;
const KNOWLEDGE_API = `${RULES_API}/knowledge`;

const STATE_CLASS = {
  completed_sop: "info-state-completed",
  verified_rules_sop_pending: "info-state-verified",
  sop_in_progress: "info-state-progress",
  pending_review: "info-state-pending",
};

const READINESS_REFERENCE = [
  ["Pending", "A required permit hasn't been recorded as received yet."],
  ["Ready", "Received with a future expiration, or explicitly marked \"does not expire\"."],
  ["Expiring", "Received but the expiration date is close."],
  ["Expired", "The expiration date has passed."],
  ["Not Ready — Expiration information required", "Received but no date and not \"does not expire\"."],
  ["811 / Locate", "Office records Valid / Trouble / Remark; JFM calculates the time condition."],
  ["Water Locate", "Ready two business days after the submission date (weekends skipped)."],
];

export default function InformationPage() {
  const [doc, setDoc] = useState(null);
  const [knowledge, setKnowledge] = useState(null);
  const [error, setError] = useState("");
  const [activeSlug, setActiveSlug] = useState(null);

  useEffect(() => {
    Promise.all([axios.get(RULES_API), axios.get(KNOWLEDGE_API)])
      .then(([rulesRes, knowledgeRes]) => {
        setDoc(rulesRes.data);
        setKnowledge(knowledgeRes.data);
        const first = knowledgeRes.data?.sections?.[0];
        if (first) setActiveSlug(first.slug);
      })
      .catch(() => setError("Information is temporarily unavailable."));
  }, []);

  const labels = knowledge?.state_labels || {};
  const sections = useMemo(() => knowledge?.sections || [], [knowledge]);
  const active = sections.find((section) => section.slug === activeSlug) || null;

  const renderRules = (groupKey) => {
    const rules = doc?.groups?.[groupKey] || [];
    return (
      <div className="information-rule-list" data-testid={`information-group-${groupKey}`}>
        {rules.map((rule) => (
          <article className="information-rule" data-testid={`information-rule-${rule.id}`} key={rule.id}>
            <p className="information-rule-summary">{rule.summary}</p>
            {rule.canonical_reasons?.length > 0 && (
              <p className="information-rule-states">Shows as: {rule.canonical_reasons.join(", ")}</p>
            )}
          </article>
        ))}
        <div className="information-quick-ref" data-testid="information-quick-reference">
          <h3>Readiness state meanings</h3>
          <ul>
            {READINESS_REFERENCE.map(([term, meaning]) => (
              <li key={term}><strong>{term}</strong> — {meaning}</li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const renderSop = (content) => {
    const control = content?.document_control || {};
    const parts = content?.visible?.parts || [];
    return (
      <div className="information-sop" data-testid="information-sop">
        {(control.version || control.last_reviewed_date) && (
          <p className="information-sop-control" data-testid="information-sop-control">
            {control.version ? `Version ${control.version}` : ""}
            {control.last_reviewed_date ? ` · Last reviewed ${control.last_reviewed_date}` : ""}
            {control.approved_by ? ` · Approved by ${control.approved_by}` : ""}
            {control.next_review_date ? ` · Next review ${control.next_review_date}` : ""}
          </p>
        )}
        {parts.map((part, partIndex) => (
          <section className="information-sop-part" data-testid={`information-sop-part-${partIndex}`} key={part.part || partIndex}>
            <h3 className="information-sop-part-title">{part.part}</h3>
            {(part.items || []).map((item, itemIndex) => (
              <div className="information-sop-item" key={item.heading || itemIndex}>
                <h4 className="information-sop-heading">{item.heading}</h4>
                <p className="information-sop-body">{item.body}</p>
              </div>
            ))}
          </section>
        ))}
      </div>
    );
  };

  const renderRelated = (section) => {
    const related = section.related || [];
    if (!related.length) return null;
    const titleFor = (slug) =>
      (sections.find((s) => s.slug === slug) || {}).title || slug;
    return (
      <aside className="information-related" data-testid={`information-related-${section.slug}`}>
        <h3 className="information-related-title">Related information</h3>
        {related.map((item, index) => (
          <div className="information-related-item" data-testid={`information-related-item-${section.slug}-${index}`} key={item.title || index}>
            <h4 className="information-related-heading">{item.title}</h4>
            <p className="information-related-body">{item.summary}</p>
            {item.governed_by && (
              <p className="information-related-governed" data-testid={`information-related-governed-${section.slug}-${index}`}>
                Governed by {item.governed_by} (Guardian rule)
              </p>
            )}
            {item.related_rule && (
              <p className="information-related-governed" data-testid={`information-related-relatedrule-${section.slug}-${index}`}>
                Related security rule: {item.related_rule}
              </p>
            )}
            {(item.see_also || []).length > 0 && (
              <p className="information-related-seealso">
                See also:{" "}
                {item.see_also.map((slug, i) => (
                  <span key={slug}>
                    {i > 0 ? " · " : ""}
                    <button
                      className="information-related-link"
                      data-testid={`information-related-link-${section.slug}-${slug}`}
                      onClick={() => setActiveSlug(slug)}
                      type="button"
                    >
                      {titleFor(slug)}
                    </button>
                  </span>
                ))}
              </p>
            )}
          </div>
        ))}
      </aside>
    );
  };

  const renderBody = (section) => {
    const source = section.content_source;
    if (source && source.startsWith("sop:") && section.sop_content) {
      return renderSop(section.sop_content);
    }
    if (source && source.startsWith("rules_doc:")) {
      const groupKey = source.split(":")[1];
      return (
        <>
          {section.summary && <p className="information-section-summary">{section.summary}</p>}
          {renderRules(groupKey)}
        </>
      );
    }
    if (section.status === "pending_review") {
      return (
        <>
          <div className="information-pending" data-testid={`information-pending-${section.slug}`}>
            <p><strong>Pending Review.</strong> This area has not been inspected or documented yet.</p>
            <p>To avoid inaccurate guidance, no procedures or system behavior are shown here. It will be documented after we review and verify this system.</p>
          </div>
          {renderRelated(section)}
        </>
      );
    }
    return (
      <>
        <div className="information-progress" data-testid={`information-progress-${section.slug}`}>
          {section.summary && <p className="information-section-summary">{section.summary}</p>}
          <p className="information-progress-note">The full SOP is being written and will appear here once reviewed and approved.</p>
        </div>
        {renderRelated(section)}
      </>
    );
  };

  return (
    <section className="information-page information-center" data-testid="information-page">
      <header className="information-header">
        <span className="operations-kicker"><BookOpen size={14} /> Information</span>
        <h1>JFM Operations Knowledge &amp; SOP Center</h1>
        <p className="information-lede">
          How to use JFM Operations and how the system works. Verified information is shown now;
          full SOPs are added as each system is reviewed and approved. This center never controls
          operational behavior.
        </p>
      </header>

      {error && <p className="information-error" data-testid="information-error">{error}</p>}

      {knowledge && (
        <div className="information-layout">
          <nav className="information-index" data-testid="information-index">
            {sections.map((section) => (
              <button
                className={`information-index-item ${section.slug === activeSlug ? "is-active" : ""}`}
                data-testid={`information-nav-${section.slug}`}
                key={section.slug}
                onClick={() => setActiveSlug(section.slug)}
                type="button"
              >
                <span className="information-index-title">{section.title}</span>
                <span className={`information-state-pill ${STATE_CLASS[section.status] || ""}`}>
                  {labels[section.status] || section.status}
                </span>
              </button>
            ))}
          </nav>

          <div className="information-content" data-testid="information-content">
            {active ? (
              <article data-testid={`information-section-${active.slug}`}>
                <div className="information-section-head">
                  <h2>{active.title}</h2>
                  <span className={`information-state-pill ${STATE_CLASS[active.status] || ""}`}>
                    {labels[active.status] || active.status}
                  </span>
                </div>
                {renderBody(active)}
              </article>
            ) : (
              <p className="information-section-summary">Select a section to open it.</p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
